import { useEffect, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import api from "../../lib/api";
import { Users, BookOpen, FileQuestion, Tag, BarChart3, ChevronRight } from "lucide-react";

export default function AdminLayout() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const navCls = ({ isActive }) =>
    `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
      isActive ? "bg-[color:var(--brand-blue)] text-white" : "hover:bg-accent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="container-x py-8 grid lg:grid-cols-[230px_1fr] gap-8" data-testid="admin-layout">
      <aside className="lg:sticky lg:top-24 lg:self-start space-y-2">
        <Link to="/admin" className="font-display text-lg flex items-center gap-2 mb-4">
          <span className="rounded bg-foreground text-background text-xs px-2 py-0.5 font-mono">ADMIN</span>
          BeforeExam
        </Link>
        <NavLink to="/admin" end className={navCls} data-testid="admin-nav-overview"><BarChart3 className="h-4 w-4" /> Overview</NavLink>
        <NavLink to="/admin/courses" className={navCls} data-testid="admin-nav-courses"><BookOpen className="h-4 w-4" /> Courses</NavLink>
        <NavLink to="/admin/questions" className={navCls} data-testid="admin-nav-questions"><FileQuestion className="h-4 w-4" /> Questions</NavLink>
        <NavLink to="/admin/coupons" className={navCls} data-testid="admin-nav-coupons"><Tag className="h-4 w-4" /> Coupons</NavLink>

        {stats && (
          <div className="mt-6 rounded-lg border border-border p-4 text-xs space-y-2">
            <div className="flex justify-between"><span className="text-muted-foreground">Users</span><b>{stats.users}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Courses</span><b>{stats.courses}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Questions</span><b>{stats.questions}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Attempts</span><b>{stats.attempts}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><b className="text-emerald-600">${stats.revenue.toFixed(2)}</b></div>
          </div>
        )}
      </aside>
      <main><Outlet /></main>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-3xl tracking-tight">Admin overview</h1>
      <p className="text-muted-foreground mt-1 text-sm">Operational pulse of BeforeExam.</p>

      {stats && (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "Total users", value: stats.users, icon: <Users className="h-5 w-5" /> },
            { label: "Courses", value: stats.courses, icon: <BookOpen className="h-5 w-5" /> },
            { label: "Questions", value: stats.questions, icon: <FileQuestion className="h-5 w-5" /> },
            { label: "Attempts", value: stats.attempts, icon: <BarChart3 className="h-5 w-5" /> },
            { label: "Enrollments", value: stats.enrollments, icon: <Users className="h-5 w-5" /> },
            { label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, icon: <Tag className="h-5 w-5" /> },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider font-bold">
                {s.icon} {s.label}
              </div>
              <div className="mt-2 font-display text-3xl">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {[
          { to: "/admin/courses", t: "Manage courses", d: "Create, update, publish certifications." },
          { to: "/admin/questions", t: "Manage questions", d: "Add questions or import from CSV." },
          { to: "/admin/coupons", t: "Coupons & flash sales", d: "Run pricing experiments." },
        ].map((c) => (
          <NavLink key={c.to} to={c.to} className="rounded-xl border border-border bg-card p-5 hover:bg-accent transition flex">
            <div>
              <div className="font-display text-lg">{c.t}</div>
              <div className="text-sm text-muted-foreground">{c.d}</div>
            </div>
            <ChevronRight className="ml-auto h-5 w-5 self-center text-muted-foreground" />
          </NavLink>
        ))}
      </div>
    </div>
  );
}
